/**
 * Action registry (theme spec 7.4 rule 3, S0.7). One named verb, many
 * triggers: menu bar items, toolbar buttons, right-click menus and the
 * active skin's keymap all point at action ids, so each skin's menu and
 * key layout is data and the handler is written once.
 *
 * Ids are dotted: `<area>.<verb>` (`appt.break`, `patient.select`,
 * `shell.palette`). The shell registers, for every nav item, `nav.<module>`
 * and `nav.<module>.<route>`; screens register their verbs with
 * `useAction()` while mounted. A palette command id is also an action id:
 * `runAction()` falls back to the command registry, so manifest commands
 * work in menus and keymaps without re-registering.
 *
 * Existing `useShortcut()` calls keep working unchanged (the Velora keymap
 * is "what the code binds today"); a screen migrates a verb by registering
 * it here and naming its Velora keys in `keymaps/velora.ts`.
 */
import type { TermKey } from '@velora/contracts/skins/terms';
import { useEffect, useRef } from 'react';
import { commandRegistry } from '../commands/registry.js';
import { createRegistry } from '../registry/store.js';

export interface ShellActionDef {
  /** `<area>.<verb>`; re-registering an id replaces it while mounted. */
  id: string;
  /** Velora wording; skins rename it through `labelKey`. */
  label: string;
  labelKey?: TermKey;
  /** Lucide icon name (app/icons.tsx) used when a toolbar names no icon. */
  icon?: string;
  /**
   * False: keys and `runAction` do nothing. Velora Modern hides the item; the
   * familiar modes keep it on menus, toolbars and right-click menus greyed,
   * with `disabledReason` as its tooltip (the real products grey, they never
   * drop a verb).
   */
  enabled?: boolean;
  /**
   * Why the action is off, in plain words for staff ("Dentrix write-back is
   * not enabled at this office"). Shown as the greyed item's tooltip.
   */
  disabledReason?: string;
  /** Permission in the current office scope; resolved by the shell. */
  permission?: string;
  run: () => void;
}

export const actionRegistry = createRegistry<ShellActionDef>();

export function registerAction(action: ShellActionDef): () => void {
  return actionRegistry.register(action);
}

/**
 * Other names for a shared verb, so each mode's requested id resolves to the
 * one handler its module registers (requests-eaglesoft 3, requests-opendental
 * R4, requests-dentrix M-1/M-2). Alias to canonical id.
 */
export const ACTION_ALIASES: Readonly<Record<string, string>> = {
  'appt.moveToPinboard': 'appt.pinboard.move',
  'appt.pin': 'appt.pinboard.move',
  'appt.complete': 'appt.setComplete',
  'ledger.receivePayment': 'ledger.enterPayment',
  'ledger.adjustment': 'ledger.enterAdjustment',
  'ledger.payment': 'ledger.enterPayment',
  'ledger.walkout': 'checkout.walkout',
  'chart.delete': 'chart.deleteProcedure',
  // Open Dental Setup > Family/Insurance > Insurance Plans and the Eaglesoft
  // Coverage Book name the insurance worklist, which the eligibility module
  // owns (ADM-14, HA-16).
  'nav.insurance': 'nav.eligibility',
  'nav.insurance.worklist': 'nav.eligibility.worklist',
};

/** The canonical id for an alias (or the id itself). */
export function canonicalAction(id: string): string {
  return ACTION_ALIASES[id] ?? id;
}

/** An action or a palette command by id; `undefined` when neither exists. */
export function findAction(id: string): ShellActionDef | undefined {
  const own = actionRegistry.find(id) ?? actionRegistry.find(canonicalAction(id));
  if (own) return own;
  const command = commandRegistry.find(id);
  if (!command) return undefined;
  return {
    id: command.id,
    label: command.title,
    ...(command.icon ? { icon: command.icon } : {}),
    ...(command.permission ? { permission: command.permission } : {}),
    run: command.run,
  };
}

let permissionCheck: ((permission: string) => boolean) | null = null;

/**
 * The signed-in shell's answer to "may this person use `permission` in the
 * offices in scope" (null when no shell is mounted). `runAction` uses it so
 * a key or in-window button never runs a verb the chrome shows greyed.
 */
export function setActionPermissionCheck(check: ((permission: string) => boolean) | null): void {
  permissionCheck = check;
}

/**
 * Runs an action by id. Returns false when it is unknown, disabled, or
 * needs a permission the person does not hold in the current scope.
 */
export function runAction(id: string): boolean {
  const action = findAction(id);
  if (!action || action.enabled === false) return false;
  if (action.permission && permissionCheck && !permissionCheck(action.permission)) return false;
  action.run();
  return true;
}

export interface UseActionOptions {
  label: string;
  labelKey?: TermKey;
  icon?: string;
  /** False greys the item in the familiar modes (hidden in Velora Modern). */
  enabled?: boolean;
  /** The greyed item's tooltip; say what is missing, not "disabled". */
  disabledReason?: string;
  permission?: string;
}

/** Tooltip for a disabled action that gives no reason of its own. */
export const DEFAULT_DISABLED_REASON = 'Not available right now';

/** Tooltip for an action the person's role may not use in the offices in scope. */
export const NO_PERMISSION_REASON = 'Your role does not allow this at the selected office';

/**
 * Whether an action can run, and if not, why. `can` answers the person's
 * permission in the current office scope.
 */
export function actionState(
  action: Pick<ShellActionDef, 'enabled' | 'disabledReason' | 'permission'>,
  can: (permission: string) => boolean,
): { active: true } | { active: false; reason: string } {
  if (action.enabled === false) {
    return { active: false, reason: action.disabledReason || DEFAULT_DISABLED_REASON };
  }
  if (action.permission && !can(action.permission)) {
    return { active: false, reason: NO_PERMISSION_REASON };
  }
  return { active: true };
}

/**
 * Registers `id` for the lifetime of the calling component. The latest
 * handler is always used, so callers need not memoise it.
 */
export function useAction(id: string, handler: () => void, options: UseActionOptions): void {
  const ref = useRef(handler);
  ref.current = handler;
  const { label, labelKey, icon, enabled, disabledReason, permission } = options;
  useEffect(
    () =>
      registerAction({
        id,
        label,
        ...(labelKey ? { labelKey } : {}),
        ...(icon ? { icon } : {}),
        ...(enabled !== undefined ? { enabled } : {}),
        ...(disabledReason ? { disabledReason } : {}),
        ...(permission ? { permission } : {}),
        run: () => ref.current(),
      }),
    [id, label, labelKey, icon, enabled, disabledReason, permission],
  );
}

/**
 * Whether a mode keeps disabled or forbidden actions on its menus, toolbars
 * and right-click menus, greyed with the reason as the tooltip. Every
 * familiar mode does (Dentrix, Open Dental and Eaglesoft grey what does not
 * apply); Velora Modern keeps hiding them.
 */
export function greysDisabledActions(skin: string): boolean {
  return skin !== 'velora';
}
